import { useEffect, useRef } from "react";
import type { Location, NavigateFunction } from "react-router-dom";

/**
 * Despia calls window.onNotificationEvent on every push tap (foreground,
 * resume and cold start). When the push carries data.path, Despia has ALREADY
 * moved window.location to that path before this runs, so comparing against
 * window.location would always match. We compare against the router's
 * rendered location instead, and navigate with replace so Back never lands
 * on the same page twice.
 */
export function isSafeInternalPath(p: unknown): p is string {
  return typeof p === "string" && p.startsWith("/") && !p.startsWith("//");
}

type NotifEvent = { path?: unknown; data?: { path?: unknown } } | undefined;

export function useNotificationEventListener(navigate: NavigateFunction, location: Location) {
  const renderedRef = useRef(location.pathname + location.search);
  renderedRef.current = location.pathname + location.search;

  useEffect(() => {
    const w = window as unknown as { onNotificationEvent?: (e: NotifEvent) => void };
    const handler = (event: NotifEvent) => {
      const path = event?.path ?? event?.data?.path;
      if (!isSafeInternalPath(path)) return;
      if (renderedRef.current === path) return;
      navigate(path, { replace: true });
    };
    w.onNotificationEvent = handler;
    return () => {
      if (w.onNotificationEvent === handler) delete w.onNotificationEvent;
    };
  }, [navigate]);
}
